import React, { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useMediaQuery } from 'react-responsive';
import useFetchData from '../../admin/hooks/use-fetch-data';
import PlotCard from './PlotCard';
import FullScreenPlan from './FullScreenPlan';
import Actions from './Actions';
import Controls from './Controls';
import Filters from './Filters';
import Key from './Key';
import Marker from './Marker';
import Floorplan from './Floorplan';
import Hotspot from './Hotspot';
import LoadingSpinner from './LoadingSpinner';
import { filtersCount, useWindowDimensions } from './lib';

export default function SitePlanMap({ element }) {
  // Store the active floorplan when viewing a tab which contains an apartment
  const [activeFloorplan, setActiveFloorplan] = useState('');
  // Store the actively applied filtering
  const [activeFilters, setActiveFilters] = useState([]);
  // State to manage the active plot card
  const [activePlot, setActivePlot] = useState(undefined);
  // State to manage whether the active plot card should show
  const [cardOpen, setCardOpen] = useState(false);
  // Toggle full screen mode
  const [fullScreen, setFullScreen] = useState(false);
  // State to manage whether the filters bar should be open or not
  const [showFilters, setShowFilters] = useState(false);
  // Number of bedrooms filters temporary selected value (before the user clicks update)
  const [bedrooms, setBedrooms] = useState([]);
  // Plot status filter temporary value
  const [status, setStatus] = useState([]);
  // Property type temporary value
  const [propType, setPropType] = useState([]);
  // Maximum price temporary value
  const [maxPrice, setMaxPrice] = useState([]);
  // Ready to move into filter temporary value
  const [readyToMoveInto, setReadyToMoveInto] = useState(false);

  // Responsive media query for desktop specific changes
  const isDesktop = useMediaQuery({ query: '(min-width: 767px)' });
  // Responsive query for changes after smaller mobile screens
  const notMobile = useMediaQuery({ query: '(min-width: 450px)' });
  // XL media query
  const isXL = useMediaQuery({ query: '(min-width: 1024px)' });
  // Pull the development post ID off of the div we render this react app to
  const developmentId = element.dataset.id;
  // Pull the development name off of the same div
  const location = element.dataset.development;
  // Optional subheading shown in the fullscreen mode
  const subheading = element.dataset?.subheading ?? '';
  // Zoom level controls
  const minZoom = isDesktop ? 1 : notMobile ? 0.5 : 0.25;
  const maxZoom = isDesktop ? 2 : 6;
  // Fetch the markers for the siteplans repeater maps for this development post ID
  const { data: sitePlans, loading: sitePlansLoading } = useFetchData({
    url: `/siteplan/get?id=${developmentId}`,
  });

  // The width of the browser window
  const { width } = useWindowDimensions();
  // The map is in a 90% width container, so calculate the true "100% width"
  const adjustedWidth = isXL ? 990 : isDesktop ? 792 : (width / 100) * 90;
  // Calculate the offset percentage for the X coordinate of all markers
  const adjustX = isXL ? 1.25 : isDesktop ? 1 : adjustedWidth / 792;
  // Calculate the height aspect ratio based on the CMS map having a width of 792px and a height of 640px
  const height = isXL ? 800 : isDesktop ? 640 : 640 * adjustX;
  // Calculate the Y offset for all plotted markers
  const adjustY = isXL ? 1.25 : isDesktop ? 1 : height / 640;

  // Reset all filters
  const resetFilters = () => {
    setBedrooms([]);
    setMaxPrice([]);
    setPropType([]);
    setStatus([]);
    setActiveFilters([]);
    setReadyToMoveInto(false);
  };

  // This locks panning on mobile devices, until the user taps on the map, as
  // during testing we ran into issues trying to scroll past the map
  const PanningInteractionHandler = (instance, state) => {
    if (state?.scale <= minZoom) {
      instance.setup.panning.disabled = true;
    } else {
      instance.setup.panning.disabled = false;
    }
  };

  return (
    <div className="siteplan">
      <div className="container siteplan__container">
        {sitePlansLoading || !sitePlans?.length ? (
          <LoadingSpinner />
        ) : (
          <>
            <Tabs.Root defaultValue={sitePlans?.[0]?.name}>
              <div className="siteplan__row">
                <div className="siteplan__col siteplan__col--left">
                  <h4 className="siteplan__heading">Interactive Siteplan</h4>
                </div>

                <div className="siteplan__col siteplan__col--right">
                  <Tabs.List className="siteplan__tabs">
                    {sitePlans?.map((siteplan, index) => (
                      <Tabs.Trigger
                        className="siteplan__button"
                        key={`siteplan-button-${index}`}
                        onClick={() => {
                          resetFilters();
                          setActiveFloorplan();
                        }}
                        value={siteplan.name}
                      >
                        {siteplan.name}
                      </Tabs.Trigger>
                    ))}
                  </Tabs.List>
                </div>
              </div>

              <div className="siteplan__maps">
                {sitePlans?.map((plan) => (
                  <Tabs.Content
                    className="siteplan__outer"
                    key={plan.name}
                    value={plan.name}
                  >
                    <div className="siteplan__wrapper">
                      <TransformWrapper
                        centerOnInit
                        initialScale={isDesktop || isXL ? 1 : 1.25}
                        initialPositionX={0}
                        initialPositionY={0}
                        maxScale={maxZoom}
                        minScale={minZoom}
                        wheel={{
                          disabled: true,
                        }}
                        onZoomStop={(ref) => {
                          PanningInteractionHandler(ref.instance, ref.state);
                        }}
                        onPanning={(ref) => {
                          PanningInteractionHandler(ref.instance, ref.state);
                        }}
                        onInit={(ref) => {
                          ref.instance.setup.panning.disabled = true;
                        }}
                      >
                        {({ zoomIn, zoomOut, instance, state }) => (
                          <>
                            <Actions
                              activeFloorplan={activeFloorplan}
                              filterCount={filtersCount(activeFilters)}
                              minZoom={isDesktop || isXL ? 1 : 0.75}
                              plan={plan}
                              setActiveFilters={setActiveFilters}
                              setActiveFloorplan={setActiveFloorplan}
                              setShowFilters={setShowFilters}
                              zoomOut={zoomOut}
                            />
                            <Controls
                              fullScreen={fullScreen}
                              setFullScreen={setFullScreen}
                              zoomIn={() => {
                                zoomIn();
                                instance.setup.panning.disabled = false;
                              }}
                              zoomOut={() => {
                                zoomOut();
                                PanningInteractionHandler(instance, state);
                              }}
                            />

                            {plan.type !== 'apartment' && plan.image && (
                              <TransformComponent>
                                <div
                                  className="siteplan__pan"
                                  style={{
                                    minHeight: height,
                                    minWidth: adjustedWidth,
                                  }}
                                >
                                  <div
                                    className="siteplan__map"
                                    style={{
                                      backgroundImage: `url(${plan.image})`,
                                    }}
                                  >
                                    {plan.markers.map((plot) => (
                                      <div
                                        className="siteplan__marker-item"
                                        key={plot.label}
                                      >
                                        {plot?.type === 'apartment' ||
                                        plot?.type === 'hotspot' ? (
                                          <Hotspot
                                            adjustY={adjustY}
                                            adjustX={adjustX}
                                            multiplier={
                                              isXL
                                                ? 1.25
                                                : isDesktop
                                                ? 1
                                                : undefined
                                            }
                                            plot={plot}
                                            resetFilters={resetFilters}
                                          />
                                        ) : (
                                          <Marker
                                            activeFilters={activeFilters}
                                            adjustY={adjustY}
                                            adjustX={adjustX}
                                            key={plot.id}
                                            marker={plot}
                                            multiplier={
                                              isXL
                                                ? 1.25
                                                : isDesktop
                                                ? 1
                                                : undefined
                                            }
                                            setActivePlot={setActivePlot}
                                            setCardOpen={setCardOpen}
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </TransformComponent>
                            )}

                            {plan.type === 'apartment' &&
                              plan?.floorplans?.length > 0 && (
                                <TransformComponent
                                  className="siteplan__pan"
                                  style={{
                                    minHeight: height,
                                    minWidth: adjustedWidth,
                                  }}
                                >
                                  <Floorplan
                                    activeFilters={activeFilters}
                                    activeFloorplan={
                                      activeFloorplan ?? undefined
                                    }
                                    adjustY={adjustY}
                                    adjustX={adjustX}
                                    height={height}
                                    multiplier={
                                      isXL ? 1.25 : isDesktop ? 1 : undefined
                                    }
                                    plans={plan.floorplans}
                                    setActivePlot={setActivePlot}
                                    setCardOpen={setCardOpen}
                                    width={adjustedWidth}
                                  />
                                </TransformComponent>
                              )}
                          </>
                        )}
                      </TransformWrapper>
                      <Filters
                        activeFilters={activeFilters}
                        bedrooms={bedrooms}
                        maxPrice={maxPrice}
                        plan={plan}
                        propType={propType}
                        resetFilters={resetFilters}
                        setActiveFilters={setActiveFilters}
                        setBedrooms={setBedrooms}
                        setMaxPrice={setMaxPrice}
                        setShowFilters={setShowFilters}
                        setPropType={setPropType}
                        setStatus={setStatus}
                        setReadyToMoveInto={setReadyToMoveInto}
                        showFilters={showFilters}
                        status={status}
                        readyToMoveInto={readyToMoveInto}
                      />
                      <PlotCard
                        plot={activePlot}
                        isOpen={cardOpen}
                        onPlotChange={setActivePlot}
                        onPlotClose={setCardOpen}
                      />
                    </div>
                  </Tabs.Content>
                ))}
                <Key />
              </div>
              <FullScreenPlan
                activeFilters={activeFilters}
                activeFloorplan={activeFloorplan}
                bedrooms={bedrooms}
                heading={location}
                fullScreen={fullScreen}
                isDesktop={isDesktop}
                maxPrice={maxPrice}
                propType={propType}
                resetFilters={resetFilters}
                setActiveFilters={setActiveFilters}
                setActiveFloorplan={setActiveFloorplan}
                setBedrooms={setBedrooms}
                setFullScreen={setFullScreen}
                setMaxPrice={setMaxPrice}
                setPropType={setPropType}
                setShowFilters={setShowFilters}
                setStatus={setStatus}
                setReadyToMoveInto={setReadyToMoveInto}
                showFilters={showFilters}
                sitePlans={sitePlans}
                status={status}
                readyToMoveInto={readyToMoveInto}
                subheading={subheading}
              />
            </Tabs.Root>
          </>
        )}
      </div>
    </div>
  );
}
